import { MyProductsApi } from '../types/api/my-products-api';
import { BaseRouteProps } from '../types/route-props';
import { ATG_MY_PRODUCTS_REST_URL, CartVersion, SPRYKER_MY_PRODUCTS_REST_URL } from '../constants';
import { TMyProductsAddResponse, TMyProductsGetResponse } from '../types/my-products';
import { Method, callAxios, AxiosRequestConfig } from '../axios';
import { buildURL, isV3, productCartPageVersion } from '../common';
import { getBrandName } from '../locale';

export const getMyProductsCrudUrl = ({
  contextRoot,
  countryCode,
  languageCode,
  urlRequestMethod,
  id,
}: BaseRouteProps & { urlRequestMethod: string; id?: string }): string => {
  let myProductsRequestUrl = '';
  if (contextRoot) {
    // eslint-disable-next-line no-param-reassign
    contextRoot = `/${contextRoot}`;
  }
  switch (urlRequestMethod) {
    case 'getOrPost':
      myProductsRequestUrl = isV3() && productCartPageVersion === CartVersion.v3
        ? SPRYKER_MY_PRODUCTS_REST_URL
        : buildURL({
          host: '',
          contextRoot,
          countryCode,
          languageCode,
          endpoint: ATG_MY_PRODUCTS_REST_URL,
        });
      break;
    case 'getByIdOrDelete':
      myProductsRequestUrl = isV3() && productCartPageVersion === CartVersion.v3
        ? `${SPRYKER_MY_PRODUCTS_REST_URL}/${id}`
        : buildURL({
          host: '',
          contextRoot,
          countryCode,
          languageCode,
          endpoint: `${ATG_MY_PRODUCTS_REST_URL}/${id}`,
        });
      break;
    default:
      myProductsRequestUrl = isV3() && productCartPageVersion === CartVersion.v3
        ? SPRYKER_MY_PRODUCTS_REST_URL
        : buildURL({
          host: '',
          contextRoot,
          countryCode,
          languageCode,
          endpoint: ATG_MY_PRODUCTS_REST_URL,
        });
  }
  return myProductsRequestUrl;
};

export const myProductsApiFetchers = {
  postMyProducts: async ({
    contextRoot,
    countryCode,
    languageCode,
    payload,
  }: BaseRouteProps & {
    payload: MyProductsApi.PostMyProductsPayload | MyProductsApi.SprykerPostMyProductsPayload;
  }) => {
    const urlRequestMethod = 'getOrPost';
    const POST_MY_PRODUCTS_URL = getMyProductsCrudUrl({
      contextRoot,
      countryCode,
      languageCode,
      urlRequestMethod,
    });

    const requestSettings: AxiosRequestConfig<any> = {
      method: Method.POST,
      url: POST_MY_PRODUCTS_URL,
      withCredentials: true,
      data: payload,
    };

    if (isV3() && productCartPageVersion === CartVersion.v3) {
      requestSettings.params = {
        'country-code': countryCode,
        'language-code': languageCode,
        brand: getBrandName(),
      };
    }
    const response = await callAxios<{ payload: typeof payload; response: TMyProductsAddResponse }>(
      requestSettings,
    );

    return response.data;
  },
  getMyProducts: async ({
    contextRoot,
    countryCode,
    languageCode,
    params,
  }: BaseRouteProps & {
    params?: MyProductsApi.GetMyProductsParams;
  }) => {
    const urlRequestMethod = 'getOrPost';
    const API_GET_MY_PRODUCTS = getMyProductsCrudUrl({
      contextRoot,
      countryCode,
      languageCode,
      urlRequestMethod,
    });

    const requestSettings: AxiosRequestConfig<unknown> = {
      method: Method.GET,
      url: API_GET_MY_PRODUCTS,
      withCredentials: true,
    };

    if (isV3() && productCartPageVersion === CartVersion.v3) {
      requestSettings.params = {
        'country-code': countryCode,
        'language-code': languageCode,
        brand: getBrandName(),
        ...params,
      };
    } else {
      requestSettings.params = params;
    }
    const response = await callAxios<{
      response: TMyProductsGetResponse;
    }>(requestSettings);

    return response.data;
  },
  deleteMyProducts: async ({
    contextRoot,
    countryCode,
    languageCode,
    params: { id },
  }: BaseRouteProps & {
    params: MyProductsApi.DeleteMyProductsParams;
  }) => {
    const urlRequestMethod = 'getByIdOrDelete';
    const API_DELETE_MY_PRODUCTS = getMyProductsCrudUrl({
      contextRoot,
      countryCode,
      languageCode,
      urlRequestMethod,
      id,
    });

    const requestSettings: AxiosRequestConfig<unknown> = {
      method: Method.DELETE,
      url: API_DELETE_MY_PRODUCTS,
      withCredentials: true,
    };

    if (isV3() && productCartPageVersion === CartVersion.v3) {
      requestSettings.params = {
        'country-code': countryCode,
        'language-code': languageCode,
        brand: getBrandName(),
      };
    }

    const response = await callAxios(requestSettings);

    return response.data;
  },
};
